import React from "react";
import {
  Text,
  Paper,
  Avatar,
  Loader,
  Alert,
  Badge,
  Group,
  Box,
} from "@mantine/core";
import { IconUser, IconAlertCircle } from "@tabler/icons-react";
import { Employee } from "../../types/salon";
import { TimeTrackingState } from "../../types/timeTracking";
import styles from "./TimeTrackingPage.module.css";

interface StaffSelectionViewProps {
  employees: Employee[];
  employeeStatuses: Map<string, TimeTrackingState>;
  selectedEmployeeId: string | null;
  isLoading: boolean;
  onSelectEmployee: (employee: Employee) => void;
}

/**
 * Get status badge properties based on time tracking state
 */
function getStatusBadgeProps(status: TimeTrackingState | undefined): {
  color: string;
  label: string;
} {
  if (!status) {
    return { color: "gray", label: "Not Clocked In" };
  }
  if (status.isOnBreak) {
    return { color: "burntOrange", label: "On Break" };
  }
  if (status.isClockedIn) {
    return { color: "teal", label: "Clocked In" };
  }
  return { color: "gray", label: "Not Clocked In" };
}

export default function StaffSelectionView({
  employees,
  employeeStatuses,
  selectedEmployeeId,
  isLoading,
  onSelectEmployee,
}: StaffSelectionViewProps) {
  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <Loader size="lg" color="teal" />
        <Text c="dimmed">Loading staff...</Text>
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Alert
        color="yellow"
        title="No Eligible Staff"
        icon={<IconAlertCircle />}
      >
        No staff members are currently set up for time tracking. Staff must be
        synced with payroll before they can use time tracking.
      </Alert>
    );
  }

  return (
    <Box className={styles.employeeListContainer}>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconUser size={20} />
          <Text size="md" fw={500}>
            Employees
          </Text>
        </Group>
        <Badge color="gray" variant="light" size="sm">
          {employees.length}
        </Badge>
      </Group>

      <Box className={styles.employeeList}>
        {employees.map((employee) => {
          const status = employeeStatuses.get(employee.id);
          const badgeProps = getStatusBadgeProps(status);
          const isSelected = selectedEmployeeId === employee.id;

          return (
            <Paper
              key={employee.id}
              className={`${styles.employeeListItem} ${isSelected ? styles.employeeListItemSelected : ""}`}
              withBorder
              onClick={() => onSelectEmployee(employee)}
            >
              <Box className={styles.employeeListItemContent}>
                <Avatar size="md" color="gray" radius="xl">
                  <IconUser size={20} />
                </Avatar>
                <Box className={styles.employeeListInfo}>
                  <Group wrap="nowrap">
                    <Text className={styles.employeeListName} fw={500}>
                      {employee.first_name} {employee.last_name}
                    </Text>
                    <Badge
                      color={badgeProps.color}
                      variant="light"
                      size="sm"
                      className={styles.statusBadge}
                    >
                      <span
                        className={styles.statusDot}
                        data-color={badgeProps.color}
                      />
                      {badgeProps.label}
                    </Badge>
                  </Group>
                  <Text className={styles.employeeListRole}>
                    {employee.role}
                  </Text>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
